import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { FileText, Plus, Trash2, Zap } from "lucide-react";
import OtterIcon from "@/components/app/OtterIcon";

/**
 * Drafts list + Magic Mode panel.
 *
 * Props:
 *  - projectId
 *  - drafts          list of drafts to render
 *  - onOpenDraft(d)  open an existing draft in the editor
 *  - onNewDraft()    start a blank draft in the editor
 *  - onSeedDraft({ title, target_keyword, content })  open editor with a seed (Magic)
 *  - onDeleteDraft(id)
 */
export default function DraftsTab({
  projectId,
  drafts,
  onOpenDraft,
  onNewDraft,
  onSeedDraft,
  onDeleteDraft,
}) {
  const [magicKw, setMagicKw] = useState("");
  const [magicBusy, setMagicBusy] = useState(false);
  const [magicStage, setMagicStage] = useState("");

  const runMagic = async () => {
    if (!magicKw.trim()) { toast.error("Enter a keyword"); return; }
    setMagicBusy(true); setMagicStage("Analyzing SERP…");
    try {
      const { data: gapData } = await api.post(`/projects/${projectId}/content/gap`, {
        target_keyword: magicKw, content: "",
      });
      const picks = gapData.missing_h2.slice(0, 6).map((h) => h.heading);
      if (picks.length === 0) {
        toast.error("No missing H2s found for this keyword");
        setMagicBusy(false); setMagicStage(""); return;
      }
      setMagicStage(`Building skeleton with ${picks.length} sections…`);
      const skeleton = `# ${magicKw}\n\n${picks.map((h) => `## ${h}\n\n[autogen]\n`).join("\n")}`;
      // Hand the editor a seed plus the keyword so the editor itself can fan-out
      // section generation (keeps Magic Mode reactive even after we navigate).
      onSeedDraft({
        title: magicKw,
        target_keyword: magicKw,
        content: skeleton,
        autoGenerateSections: picks,
      });
      toast.success("Magic draft ready — generating sections…");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Magic failed");
    }
    setMagicBusy(false); setMagicStage(""); setMagicKw("");
  };

  return (
    <div className="mt-4 space-y-3">
      <div className="border-2 border-foreground bg-white p-5" data-testid="magic-mode-panel">
        <div className="flex items-center gap-2">
          <Zap className="w-4 h-4 text-primary" />
          <h3 className="font-semibold tracking-tight text-lg">Magic Mode</h3>
          <span className="font-mono-data text-[10px] uppercase tracking-widest text-primary border border-primary px-1.5 py-0.5">new</span>
        </div>
        <p className="text-sm text-muted-foreground mt-1">Enter a keyword. We&apos;ll run a SERP gap analysis, pick the top missing H2s, and generate a publish-ready draft — in one click.</p>
        <div className="mt-4 flex flex-col sm:flex-row gap-2">
          <Input value={magicKw} onChange={(e)=>setMagicKw(e.target.value)} disabled={magicBusy}
            placeholder="e.g. 'remote work productivity'"
            className="rounded-none h-11 border-foreground/30 focus-visible:ring-foreground flex-1"
            data-testid="magic-keyword" />
          <Button onClick={runMagic} disabled={magicBusy} className="rounded-none h-11 bg-foreground hover:bg-foreground/90 px-6" data-testid="magic-run">
            <Zap className="w-4 h-4 mr-1.5" /> {magicBusy ? "Working…" : "Magic create"}
          </Button>
        </div>
        {magicBusy && (
          <p className="mt-3 font-mono-data text-xs uppercase tracking-widest text-primary" data-testid="magic-stage">› {magicStage}</p>
        )}
      </div>

      <div className="flex justify-between items-center pt-2">
        <p className="text-sm text-muted-foreground">Or start manually.</p>
        <Button onClick={onNewDraft} variant="outline" className="rounded-none h-9" data-testid="new-draft-button">
          <Plus className="w-4 h-4 mr-1.5" /> Blank draft
        </Button>
      </div>

      {drafts.length === 0 ? (
        <div className="border border-dashed border-foreground/30 p-10 text-center" data-testid="empty-drafts">
          <div className="w-12 h-12 mx-auto bg-foreground grid place-items-center text-background mb-3">
            <OtterIcon size={30} />
          </div>
          <h3 className="font-display tracking-tight text-xl">No drafts yet — Arlo&apos;s taking a nap.</h3>
          <p className="text-sm text-muted-foreground mt-1">Hit Magic Mode above, or start a blank draft and he&apos;ll wake up swinging.</p>
          <Button onClick={onNewDraft} className="mt-5 rounded-none bg-foreground hover:bg-foreground/90" data-testid="empty-new-draft-button">
            <Plus className="w-4 h-4 mr-1.5" /> Start writing
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 -mr-px -mb-px">
          {drafts.map((d) => (
            <div key={d.id} className="border border-border bg-white -mr-px -mb-px p-5 tilt-hover" data-testid={`draft-card-${d.id}`}>
              <div className="flex items-start justify-between gap-2">
                <h4 className="font-semibold text-lg tracking-tight line-clamp-2">{d.title}</h4>
                <button onClick={()=>onDeleteDraft(d.id)} className="text-muted-foreground hover:text-destructive" data-testid={`draft-delete-${d.id}`} aria-label="Delete">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              {d.target_keyword && <Badge variant="outline" className="rounded-none font-mono-data text-[10px] uppercase mt-2">{d.target_keyword}</Badge>}
              <p className="text-xs text-muted-foreground mt-3 font-mono-data">
                {(d.content || "").split(/\s+/).filter(Boolean).length} words · updated {new Date(d.updated_at).toLocaleDateString()}
              </p>
              <button onClick={()=>onOpenDraft(d)} className="mt-4 text-sm font-medium hover:underline underline-offset-4" data-testid={`draft-open-${d.id}`}>
                Open →
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
